import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { TrendingUp, Users, Briefcase, Phone, Target, Award, ArrowUpRight } from 'lucide-react';
import api from '@/lib/api';

const COLORS = ['#2563eb', '#3b82f6', '#10b981', '#f59e0b', '#e11d48'];

export default function AnalyticsPage() {
  const [overview, setOverview] = useState(null);
  const [callsByDay, setCallsByDay] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ovRes, cbdRes] = await Promise.all([
          api.get('/analytics/overview'),
          api.get('/analytics/calls-by-day?days=14')
        ]);
        setOverview(ovRes.data);
        setCallsByDay(cbdRes.data || []);
      } catch (err) { logger.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-white">
        <div className="w-8 h-8 border-2 border-[#2563eb] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    { label: 'Total Jobs', value: overview?.total_jobs || 0, icon: Briefcase, color: 'text-[#2563eb]', bg: 'bg-blue-50', border: 'border-blue-100' },
    { label: 'Total Candidates', value: overview?.total_candidates || 0, icon: Users, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100' },
    { label: 'Shortlisted', value: overview?.shortlisted_candidates || 0, icon: Award, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100' },
    { label: 'Screenings Done', value: overview?.completed_calls || 0, icon: Phone, color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-100' },
    { label: 'Avg Resume Score', value: overview?.avg_resume_score ? `${overview.avg_resume_score}/10` : '—', icon: Target, color: 'text-[#2563eb]', bg: 'bg-blue-50', border: 'border-blue-100' },
    { label: 'Team Members', value: overview?.team_members || 1, icon: TrendingUp, color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-100' },
  ];

  const funnelData = [
    { name: 'Total Candidates', value: overview?.total_candidates || 0, color: '#bfdbfe' },
    { name: 'Shortlisted', value: overview?.shortlisted_candidates || 0, color: '#60a5fa' },
    { name: 'Screened', value: overview?.screened_candidates || 0, color: '#2563eb' },
    { name: 'Completed Calls', value: overview?.completed_calls || 0, color: '#1e40af' },
  ];

  return (
    <div className="p-8 bg-slate-50 min-h-full" data-testid="analytics-page" style={{ fontFamily: 'Georgia, serif' }}>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800">Analytics</h1>
        <p className="text-slate-500 text-sm mt-1">Platform usage metrics and hiring funnel</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {statCards.map((stat) => (
          <div key={stat.label} className={`bg-white border ${stat.border} rounded-xl p-5`}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-slate-500">{stat.label}</p>
              <div className={`w-9 h-9 rounded-lg ${stat.bg} flex items-center justify-center`}>
                <stat.icon className={`w-4.5 h-4.5 ${stat.color}`} />
              </div>
            </div>
            <p className="text-3xl font-bold text-slate-800">{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Screenings chart */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="text-base font-semibold text-slate-800 mb-5">Screenings (Last 14 Days)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={callsByDay}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11, fontFamily: 'Georgia, serif' }} tickFormatter={d => d.slice(5)} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 11, fontFamily: 'Georgia, serif' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, fontFamily: 'Georgia, serif', fontSize: 12 }} labelStyle={{ color: '#475569' }} />
              <Bar dataKey="total" fill="#bfdbfe" radius={[4, 4, 0, 0]} name="Total" />
              <Bar dataKey="completed" fill="#2563eb" radius={[4, 4, 0, 0]} name="Completed" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Funnel */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="text-base font-semibold text-slate-800 mb-5">Hiring Funnel</h3>
          <div className="space-y-4">
            {funnelData.map((item) => {
              const pct = funnelData[0].value > 0 ? Math.round(item.value / funnelData[0].value * 100) : 0;
              return (
                <div key={item.name}>
                  <div className="flex justify-between mb-1.5">
                    <span className="text-sm text-slate-600">{item.name}</span>
                    <span className="text-sm font-semibold text-slate-800">{item.value} <span className="text-slate-400 font-normal">({pct}%)</span></span>
                  </div>
                  <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: item.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
